import { useState, useEffect, useCallback } from "react";
import { api, runSync } from "@/lib/api";
import { rangeParams } from "@/lib/dateRanges";
import { logError } from "@/lib/logger";
import { toast } from "sonner";

// Encapsulates connection status, analytics and report data fetching + actions,
// including the GLOBAL dashboard date range. Changing the range refetches
// analytics with the computed [start, end] window.
export function useAppData() {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [report, setReport] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [range, setRangeState] = useState("all"); // default: All Time
  const [rangeLoading, setRangeLoading] = useState(false);

  const fetchAnalytics = useCallback(async (rangeValue) => {
    const { start, end } = rangeParams(rangeValue);
    return api.analytics(start, end);
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const st = await api.status();
      setStatus(st);
      if (st.connected) {
        const [a, r] = await Promise.all([
          fetchAnalytics(range).catch(() => null),
          api.getReport().catch(() => ({ report_md: null })),
        ]);
        setAnalytics(a);
        setReport(r);
      }
    } catch (error) {
      logError("Failed to load app data:", error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchAnalytics]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Change the global date range and refetch analytics for that window.
  const setRange = useCallback(
    async (rangeValue) => {
      setRangeState(rangeValue);
      setRangeLoading(true);
      try {
        setAnalytics(await fetchAnalytics(rangeValue));
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not load data for that period.");
      } finally {
        setRangeLoading(false);
      }
    },
    [fetchAnalytics]
  );

  const sync = useCallback(async () => {
    setSyncing(true);
    try {
      const res = await runSync();
      if (res.warnings?.length) res.warnings.forEach((w) => toast.warning(w));
      toast.success(`Re-synced ${res.counts.deals} deals.`);
      setAnalytics(await fetchAnalytics(range));
      setStatus(await api.status());
    } catch (e) {
      toast.error(e.detail || e.response?.data?.detail || e.message || "Sync failed.");
    } finally {
      setSyncing(false);
    }
  }, [fetchAnalytics, range]);

  const disconnect = useCallback(async () => {
    await api.disconnect();
    setStatus({ connected: false });
    setAnalytics(null);
    setReport(null);
    toast.success("Disconnected.");
  }, []);

  return {
    loading,
    status,
    analytics,
    report,
    syncing,
    range,
    rangeLoading,
    setRange,
    loadAll,
    sync,
    disconnect,
    setReport,
  };
}
